import { useMemo, useState } from "react";
import { useSeoMeta } from "@unhead/react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  Loader2,
  Lock,
  Users,
  Backpack,
  Store,
  Trophy,
  RefreshCw,
} from "lucide-react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  CartesianGrid,
} from "recharts";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BchLogo } from "@/game/components/BchLogo";
import { ThemeToggle } from "@/components/ThemeToggle";

interface Counters {
  totals: Record<string, number>;
  daily: Record<string, Record<string, number>>;
  modes: Record<string, number>;
  startedAt: string;
}

export default function StatsPage() {
  const [passcode, setPasscode] = useState("");
  const [data, setData] = useState<Counters | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useSeoMeta({
    title: "Stats — Survive on BCH",
    description: "Private stats dashboard for Survive on BCH.",
  });

  async function load(code: string) {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(
        `/.netlify/functions/stats?action=read&passcode=${encodeURIComponent(code)}`,
      );
      if (res.status === 401) {
        setError("Wrong passcode. Try again.");
        setData(null);
        return;
      }
      if (!res.ok) {
        setError(
          "Couldn't load stats. Make sure the site is deployed to Netlify.",
        );
        return;
      }
      const json = (await res.json()) as Counters;
      setData(json);
    } catch {
      setError(
        "Couldn't reach the stats service. This only works on the deployed Netlify site.",
      );
    } finally {
      setLoading(false);
    }
  }

  const dailyRows = useMemo(() => {
    if (!data) return [];
    return Object.entries(data.daily)
      .map(([date, ev]) => ({ date: date.slice(5), visits: ev.visit ?? 0 }))
      .sort((a, b) => a.date.localeCompare(b.date))
      .slice(-14);
  }, [data]);

  const bchStarts = data?.modes?.BCH ?? 0;
  const btcStarts = data?.modes?.BTC ?? 0;

  return (
    <div className="min-h-screen bg-background bch-glow">
      {/* App bar */}
      <header className="sticky top-0 z-10 border-b border-border/70 bg-background/80 backdrop-blur-md">
        <div className="mx-auto max-w-2xl flex items-center gap-2 px-4 py-3">
          <BchLogo className="w-8 h-8" />
          <span className="font-display font-bold text-foreground">
            Survive on <span className="text-primary">BCH</span>
          </span>
          <span className="ml-auto text-[10px] uppercase tracking-wider font-semibold text-primary bg-primary/10 rounded-full px-2.5 py-1">
            Stats
          </span>
          <ThemeToggle className="-mr-2" />
        </div>
      </header>

      <main className="mx-auto max-w-2xl px-4 py-6 pb-16">
        <Button asChild variant="ghost" size="sm" className="mb-4 -ml-2">
          <Link to="/">
            <ArrowLeft className="mr-1 h-4 w-4" /> Back to game
          </Link>
        </Button>

        <div className="mb-6">
          <h1 className="font-display text-3xl font-bold tracking-tight text-foreground">
            Stats
          </h1>
          <p className="mt-1 text-muted-foreground">
            Private dashboard — see how Survive on BCH is doing.
          </p>
        </div>

        {!data ? (
          <Card className="mx-auto max-w-md">
            <CardContent className="space-y-4 p-6">
              <div className="flex items-center gap-2 text-primary">
                <Lock className="h-5 w-5" />
                <h2 className="font-bold">Enter passcode</h2>
              </div>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (passcode.trim()) load(passcode.trim());
                }}
                className="space-y-3"
              >
                <div>
                  <Label htmlFor="passcode">Passcode</Label>
                  <Input
                    id="passcode"
                    type="password"
                    className="mt-1"
                    placeholder="••••••••"
                    value={passcode}
                    onChange={(e) => setPasscode(e.target.value)}
                    autoComplete="current-password"
                  />
                </div>
                {error && <p className="text-sm text-destructive">{error}</p>}
                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Lock className="mr-2 h-4 w-4" />
                  )}
                  View stats
                </Button>
              </form>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {/* Key numbers */}
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <StatCard
                icon={<Users className="h-5 w-5" />}
                label="Total visits"
                value={data.totals.visit ?? 0}
              />
              <StatCard
                icon={<Backpack className="h-5 w-5" />}
                label="Survival games started"
                value={data.totals.survival_start ?? 0}
              />
              <StatCard
                icon={<Store className="h-5 w-5" />}
                label="Hustle games started"
                value={data.totals.hustle_start ?? 0}
              />
              <StatCard
                icon={<Trophy className="h-5 w-5" />}
                label="Games completed"
                value={data.totals.game_complete ?? 0}
              />
              <StatCard
                icon={<Users className="h-5 w-5" />}
                label="Shared to Nostr"
                value={data.totals.share_nostr ?? 0}
              />
              <StatCard
                icon={<Users className="h-5 w-5" />}
                label="Shared to X"
                value={data.totals.share_x ?? 0}
              />
            </div>

            {/* Visits over time */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  Visits — last {dailyRows.length} days
                </CardTitle>
              </CardHeader>
              <CardContent>
                {dailyRows.length === 0 ? (
                  <p className="py-8 text-center text-sm text-muted-foreground">
                    No daily data yet.
                  </p>
                ) : (
                  <div className="h-64 w-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={dailyRows}>
                        <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                        <XAxis dataKey="date" fontSize={12} />
                        <YAxis allowDecimals={false} fontSize={12} />
                        <RechartsTooltip />
                        <Bar
                          dataKey="visits"
                          fill="hsl(142 76% 36%)"
                          radius={[4, 4, 0, 0]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* BCH vs BTC mode split */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  Survival starts by currency
                </CardTitle>
              </CardHeader>
              <CardContent>
                {bchStarts + btcStarts === 0 ? (
                  <p className="py-8 text-center text-sm text-muted-foreground">
                    No survival games started yet.
                  </p>
                ) : (
                  <div className="space-y-3">
                    <ModeBar
                      label="BCH mode 🟢"
                      value={bchStarts}
                      total={bchStarts + btcStarts}
                    />
                    <ModeBar
                      label="BTC mode 🟠"
                      value={btcStarts}
                      total={bchStarts + btcStarts}
                    />
                  </div>
                )}
              </CardContent>
            </Card>

            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>
                Counting since{" "}
                {new Date(data.startedAt).toLocaleDateString(undefined, {
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                })}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => load(passcode.trim())}
                disabled={loading}
              >
                <RefreshCw
                  className={`mr-1.5 h-4 w-4 ${loading ? "animate-spin" : ""}`}
                />
                Refresh
              </Button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function StatCard({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
}) {
  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex items-center gap-2 text-primary">{icon}</div>
        <div className="mt-2 text-3xl font-bold tabular-nums text-foreground">
          {value.toLocaleString()}
        </div>
        <div className="text-sm text-muted-foreground">{label}</div>
      </CardContent>
    </Card>
  );
}

function ModeBar({
  label,
  value,
  total,
}: {
  label: string;
  value: number;
  total: number;
}) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <div>
      <div className="mb-1 flex items-center justify-between text-sm">
        <span className="font-medium text-foreground">{label}</span>
        <span className="tabular-nums text-muted-foreground">
          {value.toLocaleString()} · {pct}%
        </span>
      </div>
      <div className="h-2.5 w-full overflow-hidden rounded-full bg-muted">
        <div
          className="h-full rounded-full bg-primary transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}
